import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  FormHelperText,
  Grid,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  SelectChangeEvent
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import projectService from '../../services/projectService';

// Stylizowany komponent do przesyłania plików
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface AddOfferFormProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  onAdd: (newOffer: any) => void;
  isEditMode?: boolean;
  initialData?: any;
  onEdit?: (offerId: string, formData: FormData) => Promise<void>;
}

interface Inquiry {
  inquiry_id: string;
  subject: string;
  package_id?: string;
  package_name?: string;
  contractor_email?: string;
  contractor_name?: string;
}

const AddOfferForm: React.FC<AddOfferFormProps> = ({
  open,
  onClose,
  projectId,
  projectName,
  onAdd,
  isEditMode = false,
  initialData = null,
  onEdit
}) => {
  // Stan formularza
  const [formData, setFormData] = useState({
    inquiry_id: '',
    package_id: '',
    package_name: '',
    contractor_email: '',
    contractor_name: '',
    offer_type: 'materiały',
    offer_date: new Date().toISOString().split('T')[0],
    offered_price: '',
    description: '',
    supervisor: '',
    status: 'Aktywna'
  });

  // Inicjalizacja formularza danymi do edycji
  useEffect(() => {
    if (isEditMode && initialData && open) {
      setFormData({
        inquiry_id: initialData.inquiry_id || '',
        package_id: initialData.package_id || '',
        package_name: initialData.package_name || '',
        contractor_email: initialData.contractor_email || '',
        contractor_name: initialData.contractor_name || '',
        offer_type: initialData.offer_type || 'materiały',
        offer_date: initialData.offer_date || new Date().toISOString().split('T')[0],
        offered_price: initialData.offered_price ? initialData.offered_price.toString() : '',
        description: initialData.description || '',
        supervisor: initialData.supervisor || '',
        status: initialData.status || 'Aktywna'
      });
      
      // Jeśli oferta nie jest powiązana z zapytaniem, ustaw customOffer na true
      setCustomOffer(!initialData.inquiry_id);
    }
  }, [isEditMode, initialData, open]);

  // Stan dla plików
  const [files, setFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);

  // Stan dla zapytań
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [customOffer, setCustomOffer] = useState(false);

  // Stan dla ładowania i błędów
  const [loading, setLoading] = useState(false);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pobieranie zapytań dla projektu
  useEffect(() => {
    if (open && projectId) {
      fetchInquiries();
    }
  }, [open, projectId]);

  const fetchInquiries = async () => {
    try {
      setInquiriesLoading(true);
      const response = await projectService.getInquiriesByProjectId(projectId);
      
      // Dodaj opcję "Własna oferta" na początku listy
      const inquiriesWithCustomOption = [
        { inquiry_id: 'custom', subject: 'Własna oferta (bez powiązania z zapytaniem)' },
        ...response
      ];
      
      setInquiries(inquiriesWithCustomOption);
    } catch (error) {
      console.error('Błąd podczas pobierania zapytań:', error);
      setError('Nie udało się pobrać listy zapytań');
    } finally {
      setInquiriesLoading(false);
    }
  };

  // Obsługa zmiany zapytania
  const handleInquiryChange = async (inquiryId: string) => {
    if (inquiryId === 'custom') {
      // Jeśli wybrano "Własna oferta", wyczyść pola powiązane z zapytaniem
      setCustomOffer(true);
      setFormData({
        ...formData,
        inquiry_id: '',
        package_id: '',
        package_name: '',
        contractor_email: '',
        contractor_name: ''
      });
    } else {
      setCustomOffer(false);
      setFormData({
        ...formData,
        inquiry_id: inquiryId
      });

      try {
        // Pobierz dane zapytania
        const response = await projectService.getInquiryDataForOffer(inquiryId);
        
        if (response.data) {
          const { inquiry, package: packageData } = response.data;

          // Aktualizuj formularz danymi z zapytania
          setFormData(prevData => ({
            ...prevData,
            inquiry_id: inquiryId,
            package_id: inquiry.package_id || '',
            package_name: inquiry.package_name || (packageData ? packageData.name : ''),
            contractor_email: inquiry.contractor_email || '',
            contractor_name: inquiry.contractor_name || ''
          }));
        }
      } catch (error) {
        console.error('Błąd podczas pobierania danych zapytania:', error);
        setError('Nie udało się pobrać danych zapytania');
      }
    }
  };

  // Obsługa zmiany pól formularza dla inputów
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Obsługa zmiany pól formularza dla selectów
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Obsługa przesyłania plików
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles([...files, ...newFiles]);
      setFileNames([...fileNames, ...newFiles.map(file => file.name)]);
    }
  };

  // Usuwanie pliku
  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    const newFileNames = [...fileNames];
    newFiles.splice(index, 1);
    newFileNames.splice(index, 1);
    setFiles(newFiles);
    setFileNames(newFileNames);
  };

  // Resetowanie formularza
  const resetForm = () => {
    setFormData({
      inquiry_id: '',
      package_id: '',
      package_name: '',
      contractor_email: '',
      contractor_name: '',
      offer_type: 'materiały',
      offer_date: new Date().toISOString().split('T')[0],
      offered_price: '',
      description: '',
      supervisor: '',
      status: 'Aktywna'
    });
    setFiles([]);
    setFileNames([]);
    setCustomOffer(false);
    setError(null);
    setSuccess(null);
  };

  // Obsługa zamknięcia formularza
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Walidacja formularza
  const validateForm = () => {
    if (!formData.package_name) {
      setError('Nazwa pakietu jest wymagana');
      return false;
    }
    if (!formData.contractor_name) {
      setError('Nazwa kontrahenta jest wymagana');
      return false;
    }
    if (!formData.offer_type) {
      setError('Rodzaj oferty jest wymagany');
      return false;
    }
    if (!formData.offer_date) {
      setError('Data oferty jest wymagana');
      return false;
    }
    return true;
  };

  // Obsługa dodawania/edycji oferty
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Przygotowanie danych oferty
      const offerData = {
        ...formData,
        project_id: projectId,
        offered_price: formData.offered_price ? parseFloat(formData.offered_price) : 0
      };
      
      // Przygotowanie formularza z plikami
      const formDataWithFiles = new FormData();
      formDataWithFiles.append('data', JSON.stringify(offerData));
      
      // Dodanie plików do formularza
      files.forEach(file => {
        formDataWithFiles.append('files', file);
      });
      
      if (isEditMode && initialData && onEdit) {
        // Tryb edycji
        await onEdit(initialData.offer_id, formDataWithFiles);
        setSuccess('Oferta została zaktualizowana pomyślnie');
      } else {
        // Tryb dodawania
        const response = await projectService.createOffer(formDataWithFiles);
        setSuccess('Oferta została dodana pomyślnie');
        onAdd(response);
      }
      
      // Zamknij formularz po krótkim opóźnieniu
      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } catch (error: any) {
      console.error(`Błąd podczas ${isEditMode ? 'aktualizacji' : 'dodawania'} oferty:`, error);
      setError(error.message || `Wystąpił błąd podczas ${isEditMode ? 'aktualizacji' : 'dodawania'} oferty`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#004273', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">{isEditMode ? 'Edytuj ofertę' : 'Dodaj nową ofertę'}</Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={handleClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, mt: 1 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Projekt: <strong>{projectName}</strong>
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" disabled={inquiriesLoading}>
                <InputLabel id="inquiry-select-label">Wybierz zapytanie</InputLabel>
                <Select
                  labelId="inquiry-select-label"
                  id="inquiry-select"
                  label="Wybierz zapytanie"
                  onChange={(e) => handleInquiryChange(e.target.value as string)}
                  defaultValue=""
                >
                  {inquiriesLoading ? (
                    <MenuItem value="">
                      <CircularProgress size={20} /> Ładowanie zapytań...
                    </MenuItem>
                  ) : (
                    inquiries.map((inquiry) => (
                      <MenuItem key={inquiry.inquiry_id} value={inquiry.inquiry_id}>
                        {inquiry.subject}
                      </MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>
                  Wybierz zapytanie, aby automatycznie wypełnić pola lub wybierz "Własna oferta"
                </FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nazwa pakietu"
                name="package_name"
                value={formData.package_name}
                onChange={handleChange}
                variant="outlined"
                required
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kontrahent"
                name="contractor_name"
                value={formData.contractor_name}
                onChange={handleChange}
                variant="outlined"
                required
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email kontrahenta"
                name="contractor_email"
                value={formData.contractor_email}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" disabled={loading}>
                <InputLabel id="offer-type-label">Rodzaj</InputLabel>
                <Select
                  labelId="offer-type-label"
                  id="offer-type"
                  name="offer_type"
                  value={formData.offer_type}
                  onChange={handleSelectChange}
                  label="Rodzaj"
                  required
                >
                  <MenuItem value="materiały">Materiały</MenuItem>
                  <MenuItem value="usługi">Usługi</MenuItem>
                </Select>
                <FormHelperText>
                  Wybierz rodzaj oferty - wpływa na folder docelowy dla plików
                </FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data oferty"
                name="offer_date"
                type="date"
                value={formData.offer_date}
                onChange={handleChange}
                variant="outlined"
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Oferowana cena (PLN)"
                name="offered_price"
                type="number"
                value={formData.offered_price}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Opiekun"
                name="supervisor"
                value={formData.supervisor}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" disabled={loading}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  label="Status"
                >
                  <MenuItem value="Aktywna">Aktywna</MenuItem>
                  <MenuItem value="Zaakceptowana">Zaakceptowana</MenuItem>
                  <MenuItem value="Odrzucona">Odrzucona</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Opis"
                name="description"
                value={formData.description}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={3}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  disabled={loading}
                >
                  Dodaj pliki
                  <VisuallyHiddenInput
                    type="file"
                    multiple
                    onChange={handleFileChange}
                  />
                </Button>
                <FormHelperText>
                  Pliki zostaną przesłane do folderu {formData.offer_type === 'materiały' ? '4_MATERIALY > 1_OFERTY' : '5_PODWYKONAWCY > 1_OFERTY'}
                </FormHelperText>
              </Box>
              
              {fileNames.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Wybrane pliki:
                  </Typography>
                  <Box sx={{ 
                    maxHeight: '150px', 
                    overflowY: 'auto', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 1 
                  }}>
                    {fileNames.map((name, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 0.5,
                          '&:not(:last-child)': {
                            borderBottom: '1px solid #f0f0f0'
                          }
                        }}
                      >
                        <Typography variant="body2" noWrap sx={{ maxWidth: '80%' }}>
                          {name}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveFile(index)}
                          disabled={loading}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">{success}</Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={loading}
        >
          Anuluj
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{ 
            bgcolor: '#004273',
            '&:hover': { bgcolor: '#003660' }
          }}
        >
          {loading ? (isEditMode ? 'Aktualizowanie...' : 'Dodawanie...') : (isEditMode ? 'Zapisz zmiany' : 'Dodaj ofertę')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOfferForm;
